import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Loader2, Search, X } from "lucide-react";

/**
 * AddressAutocomplete — globale, gratuito, basato su OpenStreetMap Nominatim.
 * Suggerisce vie/città/CAP del pianeta intero e popola automaticamente
 * via, civico, cap, città, provincia, nazione, lat, lng.
 *
 * Usage:
 *   <AddressAutocomplete
 *     value={form.indirizzo}
 *     onChange={(text) => setForm({ ...form, indirizzo: text })}
 *     onSelect={(addr) => setForm({ ...form, ...addr })}
 *     placeholder="Inizia a scrivere la via…"
 *     countrycodes="it"  // opzionale, default mondo intero
 *   />
 *
 * Restituisce all'onSelect un oggetto con:
 *   { indirizzo, via, civico, cap, citta, provincia, nazione, lat, lng, display_name }
 */

const NOMINATIM = "https://nominatim.openstreetmap.org/search";

const headers = {
  // Nominatim policy: identify the application
  "Accept-Language": "it,en",
};

function parseNominatim(item) {
  const a = item.address || {};
  const road = a.road || a.pedestrian || a.footway || a.path || "";
  const house = a.house_number || "";
  const via = road;
  const civico = house;
  const indirizzo = [road, house].filter(Boolean).join(", ");
  const citta = a.city || a.town || a.village || a.hamlet || a.municipality || "";
  const cap = a.postcode || "";
  // Provincia: in IT 'county' è la provincia; fallback su state_district / state
  const provincia =
    a["ISO3166-2-lvl6"]?.split("-")?.[1] ||
    a.county ||
    a.state_district ||
    a.state ||
    "";
  const nazione = a.country || "";
  return {
    display_name: item.display_name,
    indirizzo: indirizzo || item.display_name?.split(",")[0] || "",
    via,
    civico,
    cap,
    citta,
    provincia: provincia?.length === 2 ? provincia.toUpperCase() : provincia,
    nazione,
    lat: parseFloat(item.lat),
    lng: parseFloat(item.lon),
  };
}

export default function AddressAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Inizia a scrivere la via, città o CAP…",
  className = "",
  inputClassName = "",
  countrycodes = "", // es. 'it' per limitare all'Italia; vuoto = mondo intero
  testid,
  required = false,
  disabled = false,
}) {
  const [query, setQuery] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const wrapperRef = useRef(null);

  // sync external value -> internal
  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const fetchSuggestions = (q) => {
    if (!q || q.trim().length < 3) {
      setItems([]); setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    const params = new URLSearchParams({
      q,
      format: "json",
      addressdetails: "1",
      limit: "8",
      "accept-language": "it,en",
    });
    if (countrycodes) params.set("countrycodes", countrycodes);
    fetch(`${NOMINATIM}?${params.toString()}`, { signal: ctrl.signal, headers })
      .then((r) => r.json())
      .then((data) => {
        setItems(Array.isArray(data) ? data : []);
        setOpen(true);
        setHighlight(-1);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          setItems([]);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (v) => {
    setQuery(v);
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 350);
  };

  const pick = (item) => {
    const parsed = parseNominatim(item);
    setQuery(parsed.indirizzo || parsed.display_name);
    onChange?.(parsed.indirizzo || parsed.display_name);
    onSelect?.(parsed);
    setOpen(false);
    setItems([]);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(items[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const clear = () => {
    setQuery(""); onChange?.(""); setItems([]); setOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid={testid ? `addr-${testid}` : undefined}>
      <div className="relative">
        <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#ff5577] pointer-events-none" />
        <input
          type="text"
          value={query}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (items.length > 0) setOpen(true); }}
          onKeyDown={onKey}
          data-testid={testid ? `${testid}-input` : undefined}
          className={`w-full bg-white/[0.03] border border-white/10 rounded-lg pl-9 pr-9 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none ${inputClassName}`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader2 size={12} className="animate-spin text-white/40" />}
          {!loading && query && (
            <button type="button" onClick={clear} tabIndex={-1} className="text-white/30 hover:text-white/70">
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {open && items.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-white/40 bg-white/[0.02] border-b border-white/5 inline-flex items-center gap-2">
            <Search size={10} /> Risultati globali · OpenStreetMap
          </div>
          <div className="max-h-72 overflow-auto">
            {items.map((it, i) => {
              const p = parseNominatim(it);
              const subtitle = [p.cap, p.citta, p.provincia, p.nazione].filter(Boolean).join(" · ");
              return (
                <button
                  key={it.place_id || i}
                  type="button"
                  onClick={() => pick(it)}
                  onMouseEnter={() => setHighlight(i)}
                  data-testid={testid ? `${testid}-opt-${i}` : undefined}
                  className={`w-full text-left px-3 py-2 text-xs border-b border-white/5 last:border-0 transition-colors ${highlight === i ? "bg-[#ff0033]/[0.12]" : "hover:bg-white/[0.04]"}`}
                >
                  <div className="font-medium text-white/90 truncate">
                    {p.indirizzo || it.display_name?.split(",").slice(0, 2).join(",")}
                  </div>
                  <div className="text-[10px] text-white/45 font-mono truncate mt-0.5">{subtitle || it.display_name}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {open && !loading && items.length === 0 && query.trim().length >= 3 && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl">
          <div className="p-4 text-center text-xs text-white/40">Nessun indirizzo trovato</div>
        </div>
      )}
    </div>
  );
}
